import { useCallback, useState } from 'react';

export interface FocusIndex {
  /** The currently focused index, always within `[0, length)` (or 0 when empty). */
  index: number;
  /**
   * Move the focus by `direction` (typically ±1), clamped to `[0, length)`.
   * Moving past either end is a no-op, so the caller doesn't have to guard edges.
   */
  move: (direction: number) => void;
  /** Jump directly to `next`, clamped to `[0, length)`. */
  set: (next: number) => void;
}

const clamp = (value: number, length: number): number => {
  if (length <= 0) return 0;
  if (value < 0) return 0;
  if (value >= length) return length - 1;
  return value;
};

/**
 * A clamped selection index over a list of `length` items.
 *
 * Shared by the keyboard-navigable cards (onboarding steps, welcome categories
 * and example rows): each holds one of these per navigation axis. Moving past
 * the first/last item clamps rather than wraps, matching the cards' behaviour.
 */
export function useFocusIndex(length: number): FocusIndex {
  const [index, setIndex] = useState(0);

  const move = useCallback(
    (direction: number) => setIndex((current) => clamp(current + direction, length)),
    [length],
  );

  const set = useCallback((next: number) => setIndex(clamp(next, length)), [length]);

  return { index: clamp(index, length), move, set };
}
